import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';
import { debugOrgIdExtraction, extractOrgIdFromPath } from '../utils/organizationUtils';
import { accessControlService } from '../services/accessControl';

interface Organization {
  id: string;
  name: string;
  organisation_name?: string; // Legacy field
  tournament_access?: Array<{ id: string; name: string }>;
  team_access?: Array<{ id: string; name: string }>;
  active: boolean;
  created_at: string;
  updated_at: string;
  subdomain?: string;
}

interface Project {
  id: number;
  name: string;
  description?: string;
  organization_id: number;
  active: boolean;
  created_at: string;
  updated_at: string;
}

interface OrgContextType {
  currentOrganization: Organization | null;
  currentProject: Project | null;
  userOrganizations: Organization[];
  organizationProjects: Project[];
  isLoading: boolean;
  switchOrganization: (orgSlug: string) => Promise<void>;
  switchProject: (projectId: string) => Promise<void>;
  refreshOrganizationData: () => Promise<void>;
  loadOrganizationByUrl: (orgId: string) => Promise<Organization | null>;
  loadUserOrganization: () => Promise<Organization | null>;
  checkUserAccess: (orgId: string) => Promise<boolean>;
}

const OrgContext = createContext<OrgContextType | undefined>(undefined);

export const OrgProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [currentOrganization, setCurrentOrganization] = useState<Organization | null>(null);
  const [currentProject, setCurrentProject] = useState<Project | null>(null);
  const [userOrganizations, setUserOrganizations] = useState<Organization[]>([]);
  const [organizationProjects, setOrganizationProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  
  // Cache to prevent repeated API calls
  const [orgDataLoaded, setOrgDataLoaded] = useState(false);

    // Load organization by URL orgId with enhanced caching and deduplication
  const loadOrganizationByUrl = async (orgId: string): Promise<Organization | null> => {
    try {
      console.log('🔍 Loading organization by URL orgId:', orgId);

      // Debug the organization ID extraction
      debugOrgIdExtraction(window.location.pathname);

      // If orgId is 'default', redirect to organization selection
      if (orgId === 'default') {
        console.log('🔄 orgId is "default", should redirect to organization selection');
        return null;
      }

      // Check if we already have this organization loaded
      if (currentOrganization?.id === orgId) {
        console.log('📦 Organization already loaded, using cached data:', currentOrganization.name);
        return currentOrganization;
      }

      // Check if we're already loading this organization (prevent duplicate calls)
      if (isLoading) {
        console.log('⏳ Organization already being loaded, waiting...');
        return null;
      }

      setIsLoading(true);

      // First try to load by UUID (if it's a valid UUID)
      let { data: orgData, error } = await supabase
        .from('organisations')
        .select('*')
        .eq('id', orgId)
        .single();
      
      if (error) {
        console.log('⚠️ Organization not found by UUID, trying alternative methods...');
        
        // Try to find by partial UUID match (for truncated IDs)
        const { data: partialMatch } = await supabase
          .from('organisations')
          .select('*')
          .ilike('id', `${orgId}%`)
          .limit(1)
          .single();
        
        if (partialMatch) {
          console.log('✅ Organization found by partial UUID match:', partialMatch.name);
          setCurrentOrganization(partialMatch);
          // Sync with AccessControlService to prevent duplicate API calls
          accessControlService.setOrganizationData(partialMatch);
          setIsLoading(false);
          return partialMatch;
        }
        
        // Try to find by subdomain or name as fallback
        const { data: fallbackMatch } = await supabase
          .from('organisations')
          .select('*')
          .or(`subdomain.eq.${orgId},name.ilike.%${orgId}%`)
          .limit(1)
          .single();
        
        if (fallbackMatch) {
          console.log('✅ Organization found by fallback method:', fallbackMatch.name);
          setCurrentOrganization(fallbackMatch);
          // Sync with AccessControlService to prevent duplicate API calls
          accessControlService.setOrganizationData(fallbackMatch);
          setIsLoading(false);
          return fallbackMatch;
        }
        
        console.error('❌ Organization not found by any method');
        setIsLoading(false);
        return null;
      }
      
      if (orgData) {
        console.log('✅ Organization loaded by UUID:', orgData.name);
        setCurrentOrganization(orgData);
        // Sync with AccessControlService to prevent duplicate API calls
        accessControlService.setOrganizationData(orgData);
        setIsLoading(false);
        return orgData;
      }
      
      setIsLoading(false);
      return null;
    } catch (error) {
      console.error('❌ Error in loadOrganizationByUrl:', error);
      setIsLoading(false);
      return null;
    }
  };

  // Load user's organization when user is available
  const loadUserOrganization = async (): Promise<Organization | null> => {
    try {
      if (!user) {
        console.log('⚠️ No user available for organization loading');
        return null;
      }

      console.log('🔍 Loading user organization for user:', user.email);
      
      const { data: userOrgData, error: userOrgError } = await supabase
        .from('user_organisations')
        .select('organisation_id')
        .eq('user_id', user.id)
        .eq('is_primary', true)
        .eq('active', true)
        .single();
      
      if (userOrgError) {
        console.error('❌ Error fetching user organization:', userOrgError);
        return null;
      }
      
      if (!userOrgData?.organisation_id) {
        console.log('⚠️ User has no organization assigned');
        return null;
      }

      console.log('✅ Found user organization ID:', userOrgData.organisation_id);
      
      // Load the organization details
      const { data: orgData, error: orgError } = await supabase
        .from('organisations')
        .select('*')
        .eq('id', userOrgData.organisation_id)
        .single();
      
      if (orgError) {
        console.error('❌ Error loading organization details:', orgError);
        return null;
      }
      
      if (orgData) {
        console.log('✅ User organization loaded:', orgData.organisation_name);
        setCurrentOrganization(orgData);
        // Sync with AccessControlService to prevent duplicate API calls
        accessControlService.setOrganizationData(orgData);
        return orgData;
      }
      
      return null;
    } catch (error) {
      console.error('❌ Error in loadUserOrganization:', error);
      return null;
    }
  };

  // Check if user has access to this organization
  const checkUserAccess = async (orgId: string): Promise<boolean> => {
    try {
      if (!user) return false;
      
      console.log('🔍 Checking user access for orgId:', orgId);
      
      // Check if user belongs to this organization
      const { data: userOrgData, error } = await supabase
        .from('user_organisations')
        .select('organisation_id')
        .eq('user_id', user.id)
        .eq('organisation_id', orgId)
        .eq('active', true)
        .single();
      
      if (error) {
        console.error('❌ Error checking user access:', error);
        return false;
      }
      
      // Check if user has access to this organization
      const hasAccess = !!userOrgData?.organisation_id;
      console.log('✅ User access check result:', hasAccess);
      
      return hasAccess;
    } catch (error) {
      console.error('❌ Error in checkUserAccess:', error);
      return false;
    }
  };

  // Load organization projects
  const loadOrganizationProjects = async (orgId: string) => {
    try {
      console.log('🔍 Loading projects for organization:', orgId);
      
      const { data: projects, error } = await supabase
        .from('tournaments')
        .select('*')
        .eq('organisation_id', orgId)
        .eq('active', true);
      
      if (error) {
        console.error('❌ Error loading organization projects:', error);
        return;
      }
      
      // Convert tournaments to projects format
      const projectData = projects?.map(tournament => ({
        id: tournament.id,
        name: tournament.name,
        description: tournament.description,
        organization_id: tournament.organisation_id,
        active: tournament.active,
        created_at: tournament.created_at,
        updated_at: tournament.updated_at
      })) || [];
      
      setOrganizationProjects(projectData);
      console.log('✅ Organization projects loaded:', projectData.length);
    } catch (error) {
      console.error('❌ Error in loadOrganizationProjects:', error);
    }
  };

  // Load organization data for the current user (fallback)
  const loadOrganizationData = async () => {
    // Prevent repeated API calls
    if (orgDataLoaded || !user) {
      console.log('🚫 Skipping organization data load - already loaded or no user');
      return;
    }
    
    try {
      setIsLoading(true);
      console.log('🔄 Loading organization data for user:', user.id);
      
      // Get user's organization from user_organisations table
      const { data: userOrgData } = await supabase
        .from('user_organisations')
        .select('organisation_id')
        .eq('user_id', user.id)
        .eq('is_primary', true)
        .eq('active', true)
        .single();
      
      if (userOrgData?.organisation_id) {
        // Load the organisation data
        const { data: orgData } = await supabase
          .from('organisations')
          .select('*')
          .eq('id', userOrgData.organisation_id)
          .single();
        
        if (orgData) {
          setCurrentOrganization(orgData);
          setUserOrganizations([orgData]);
          
          // Sync with AccessControlService to prevent duplicate API calls
          accessControlService.setOrganizationData(orgData);
          
          // Load projects for this organization
          await loadOrganizationProjects(orgData.id.toString());
          
          // Mark as loaded to prevent repeated calls
          setOrgDataLoaded(true);
          console.log('✅ Organization data loaded and cached');
        }
      }
    } catch (error) {
      console.error('❌ Error loading organization data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Load organization data when user changes
  useEffect(() => {
    if (user) {
      loadOrganizationData();
    } else {
      setCurrentOrganization(null);
      setCurrentProject(null);
      setUserOrganizations([]);
      setOrganizationProjects([]);
    }
  }, [user]);

  // Centralized organization loading based on URL changes
  useEffect(() => {
    const handleUrlChange = () => {
      const orgIdFromPath = extractOrgIdFromPath(window.location.pathname);
      
      if (orgIdFromPath && orgIdFromPath !== 'default') {
        // Only load if we don't already have this organization or if it's different
        if (!currentOrganization || currentOrganization.id !== orgIdFromPath) {
          console.log('🔄 URL changed, loading organization:', orgIdFromPath);
          loadOrganizationByUrl(orgIdFromPath);
        }
      }
    };

    // Load organization on initial mount
    handleUrlChange();

    // Listen for URL changes (for SPA navigation)
    window.addEventListener('popstate', handleUrlChange);
    
    return () => {
      window.removeEventListener('popstate', handleUrlChange);
    };
  }, []); // Remove currentOrganization dependency to prevent infinite loops

  // Remove this infinite loop - it's causing excessive API calls
  // The currentOrganization state is already managed properly above

  const value: OrgContextType = {
    currentOrganization,
    currentProject,
    userOrganizations,
    organizationProjects,
    isLoading,
    switchOrganization: async (orgSlug: string) => {
      // Simple organization switching - just update the current org
      const org = userOrganizations.find(o => o.subdomain === orgSlug);
      if (org) {
        setCurrentOrganization(org);
        // Notify AccessControlService about organization change to clear cache
        accessControlService.setOrganizationData(org);
      }
    },
    switchProject: async (projectId: string) => {
      // Simple project switching
      const project = organizationProjects.find(p => p.id === parseInt(projectId));
      if (project) {
        setCurrentProject(project);
      }
    },
    refreshOrganizationData: async () => {
      await loadOrganizationData();
    },
    loadOrganizationByUrl,
    loadUserOrganization,
    checkUserAccess
  };

  return (
    <OrgContext.Provider value={value}>
      {children}
    </OrgContext.Provider>
  );
};

export const useOrg = (): OrgContextType => {
  const context = useContext(OrgContext);
  if (context === undefined) {
    throw new Error('useOrg must be used within an OrgProvider');
  }
  return context;
};
